import React, { useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  GET_COMPANY_LOGO,
  companyLogoUrl,
  removeCompanyLogo,
  uploadCompanyLogo,
} from "../../apis/company-logo-apis/companyLogoApis";
import {
  LOGO_ACCEPT_ATTRIBUTE,
  LOGO_HINT,
  validateLogo,
} from "../../pages/TrustCenter/data/uploadRules";

// Upload, replace or remove the company logo. Used in Company Settings and
// in the Trust Center Overview: it is the same logo in both places, and a
// change is saved at once (it is not part of the form around it).
const CompanyLogoUpload = () => {
  const theme = useTheme();
  const client = useApolloClient();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { data, loading } = useQuery(GET_COMPANY_LOGO);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);

  const logoUrl = companyLogoUrl(data?.getCompanyLogo?.logoPath);

  const run = async (work) => {
    setBusy(true);
    setError("");
    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      await work({ accessToken, idToken });
      // Every screen that shows the logo picks up the change.
      await client.refetchQueries({
        include: ["getCompanyLogo", "getTrustCenter"],
      });
    } catch (e) {
      setError(e.message || "Could not save the logo. Try again.");
    } finally {
      setBusy(false);
      setConfirmRemove(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const problem = validateLogo(file);
    if (problem) {
      setError(problem);
      return;
    }
    run((tokens) => uploadCompanyLogo(file, tokens));
  };

  return (
    <Box>
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: theme.customColors?.text?.strong ?? "#374151",
          mb: 0.75,
        }}
      >
        Company logo
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 120,
            height: 64,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            border: `1px dashed ${theme.customColors.border.dashed}`,
            backgroundColor: theme.customColors.background.infoBox,
            overflow: "hidden",
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt="Company logo"
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <ImageOutlinedIcon
              sx={{ color: theme.customColors.icons.secondary }}
            />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={busy || loading}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {busy ? "Saving…" : logoUrl ? "Replace logo" : "Upload logo"}
              <input
                hidden
                type="file"
                aria-label="Upload company logo"
                accept={LOGO_ACCEPT_ATTRIBUTE}
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  // Let the same file be chosen again after an error.
                  e.target.value = "";
                }}
              />
            </Button>
            {logoUrl && (
              <Button
                size="small"
                color="error"
                disabled={busy}
                onClick={() =>
                  confirmRemove
                    ? run(removeCompanyLogo)
                    : setConfirmRemove(true)
                }
                sx={{ textTransform: "none" }}
              >
                {confirmRemove ? "Yes, remove it" : "Remove"}
              </Button>
            )}
          </Stack>
          <Typography
            variant="caption"
            role={error ? "alert" : undefined}
            sx={{
              display: "block",
              mt: 0.5,
              color: error
                ? theme.customColors.status.error.text
                : theme.customColors.text.secondary,
            }}
          >
            {error ||
              `${LOGO_HINT}. Shown in Company Settings and on your Trust Center. Saved as soon as you upload.`}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default CompanyLogoUpload;
